import Select from 'ui/src/components/Select'
import { useTranslation } from 'react-i18next'
import { Language } from '@mui/icons-material'
import Box from 'ui/src/components/Box'
import { useLocation, useNavigate } from 'react-router-dom'
import MenuItem from '../MenuItem'

const languages = [
  { key: 'en-US', value: 'English', shortValue: 'EN' },
  { key: 'fr-FR', value: 'French', shortValue: 'FR' },
]

interface LanguageSelectorProps {
  onChange?(language: string): Promise<void>
  iconSize?: string
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ onChange, iconSize }) => {
  const { i18n } = useTranslation()

  const navigate = useNavigate()
  const location = useLocation()

  const handleChange = async e => {
    const language = e.target.value || 'en-US'
    i18n.changeLanguage(language)
    if (onChange) await onChange(language)

    const searchParams = new URLSearchParams(location.search)
    searchParams.delete('lang')

    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    })

    window.location.reload()
  }

  const verifyLanguageExist = lang => languages.find(l => l.key === lang)

  return (
    <Select
      onChange={handleChange}
      value={i18n.language && verifyLanguageExist(i18n.language) ? i18n.language : 'en-US'}
      MenuProps={{
        slotProps: {
          paper: {
            elevation: 3,
            sx: {
              '& .MuiMenuItem-root': { fontSize: 14, fontWeight: 500 },
              '& .Mui-selected': {
                color: 'primary.600',
                backgroundColor: 'gray.0',
                '&:focus': {
                  backgroundColor: 'gray.0',
                },
              },
              marginTop: '5px',
            },
          },
        },
      }}
      sx={{
        boxShadow: 'none',
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&.Mui-focused .MuiOutlinedInput-root': {
          border: 'none',
        },
        '&.Mui-focused': { backgroundColor: 'primary.50' },
        color: 'primary.600',
        fontWeight: 'subtitle1.fontWeight',
        fontSize: 'subtitle1.fontSize',
        '& .icon-margin-right': {
          marginRight: '5px',
          fontSize: iconSize ? iconSize : 14,
        },
      }}
      startAdornment={<Language className="icon-margin-right" />}
    >
      {languages.map(lang => (
        <MenuItem key={lang.value} value={lang.key}>
          <Box
            sx={{
              display: {
                xl: 'flex',
                lg: 'flex',
                md: 'flex',
                sm: 'flex',
                xs: 'none',
              },
            }}
          >
            {lang.value}
          </Box>
          <Box
            sx={{
              display: {
                xl: 'none',
                lg: 'none',
                md: 'none',
                sm: 'none',
                xs: 'flex',
              },
            }}
          >
            {lang.shortValue}
          </Box>
        </MenuItem>
      ))}
    </Select>
  )
}

export default LanguageSelector
